import React, { useEffect, useState } from 'react';
import { Timeline, Card, Tag, Typography, Space, Spin, Empty } from 'antd';
import {
  ClockCircleOutlined,
  ArrowUpOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  FlagOutlined
} from '@ant-design/icons';
import { hazardService } from '@iso/services/hazard.service';
import { getEscalationLevelShortLabel } from '@iso/constants/hazardStatus';
import moment from 'moment';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

const { Text } = Typography;

/**
 * Hazard Timeline Component
 * Displays a vertical timeline of all hazard events (escalations, assignments, status changes)
 */
const HazardTimeline = ({ hazardId }) => {
  const [loading, setLoading] = useState(false);
  const [escalations, setEscalations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [timelineItems, setTimelineItems] = useState([]);

  useEffect(() => {
    if (hazardId) {
      fetchTimelineData();
    }
  }, [hazardId]);

  const fetchTimelineData = async () => {
    setLoading(true);
    try {
      const [escalationRes, assignmentRes] = await Promise.all([
        hazardService.getEscalationHistory(hazardId),
        hazardService.getAssignmentHistory(hazardId)
      ]);

      const escalationData = escalationRes?.escalations || [];
      const assignmentData = assignmentRes?.assignments || [];

      setEscalations(escalationData);
      setAssignments(assignmentData);

      // Combine and sort all events by timestamp
      buildTimeline(escalationData, assignmentData);
    } catch (error) {
      console.error('Failed to fetch timeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildTimeline = (escalationData, assignmentData) => {
    const events = [];

    // Add escalation events
    escalationData.forEach(escalation => {
      events.push({
        type: 'escalation',
        timestamp: escalation.escalated_at,
        data: escalation
      });
    });

    // Add assignment events
    assignmentData.forEach(assignment => {
      events.push({
        type: 'assignment',
        timestamp: assignment.assigned_at,
        data: assignment
      });
    });

    // Sort by timestamp (most recent first)
    events.sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp)));

    // Build timeline items
    const items = events.map((event, index) => {
      if (event.type === 'escalation') {
        return buildEscalationItem(event.data, index);
      } else {
        return buildAssignmentItem(event.data, index);
      }
    });

    setTimelineItems(items);
  };

  const buildEscalationItem = (escalation, index) => {
    const isAutomatic = escalation.escalation_type === 'automatic';
    const color = escalation.to_level === 3 ? 'red' : escalation.to_level === 2 ? 'orange' : 'blue';

    return {
      key: `escalation-${escalation.id}`,
      dot: <ArrowUpOutlined style={{ fontSize: '16px', color }} />,
      color,
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Text strong>Escalated</Text>
            <Tag color={color}>
              {getEscalationLevelShortLabel(escalation.from_level)} → {getEscalationLevelShortLabel(escalation.to_level)}
            </Tag>
            <Tag color={isAutomatic ? 'default' : 'purple'}>
              {isAutomatic ? 'Automatic' : 'Manual'}
            </Tag>
            {escalation.to_level === 3 && (
              <Tag color="red">URGENT</Tag>
            )}
          </Space>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {commonHelper.displayDateTimeLocal(escalation.escalated_at)}
          </Text>

          {escalation.escalation_reason && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              <i>Reason: {escalation.escalation_reason}</i>
            </Text>
          )}

          {escalation.escalated_by_user && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              By: {escalation.escalated_by_user.name}
            </Text>
          )}

          {escalation.new_assignee_ids && escalation.new_assignee_ids.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Assigned to {escalation.new_assignee_ids.length} user(s)
            </Text>
          )}

          <Space size="small">
            <Text type="secondary" style={{ fontSize: 12 }}>
              Previous deadline:
            </Text>
            <Text style={{ fontSize: 12 }}>
              {commonHelper.displayDateTimeLocal(escalation.previous_deadline)}
            </Text>
          </Space>

          <Space size="small">
            <Text type="secondary" style={{ fontSize: 12 }}>
              New deadline:
            </Text>
            <Text strong style={{ fontSize: 12 }}>
              {commonHelper.displayDateTimeLocal(escalation.new_deadline)}
            </Text>
          </Space>
        </Space>
      )
    };
  };

  const buildAssignmentItem = (assignment, index) => {
    const isEscalation = assignment.assignment_type === 'escalation';
    const isInitial = assignment.assignment_type === 'initial';
    const isClosed = assignment.assignment_type === 'closed';
    const statusColors = {
      active: 'green',
      completed: 'blue',
      overdue: 'red',
      escalated: 'orange'
    };

    return {
      key: `assignment-${assignment.id}`,
      dot: <UserOutlined style={{ fontSize: '16px' }} />,
      color: statusColors[assignment.status] || 'gray',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Text strong>
              {isInitial && 'Initial Assignment'}
              {isEscalation && 'Escalation Assignment'}
              {isClosed && 'Closed Hazard Assignment'}
              {!isInitial && !isEscalation && !isClosed && 'Manual Assignment'}
            </Text>
            <Tag color={statusColors[assignment.status]}>
              {assignment.status.replace('_', ' ').toUpperCase()}
            </Tag>
            <Tag>
              {getEscalationLevelShortLabel(assignment.assignment_level)}
            </Tag>
          </Space>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {commonHelper.displayDateTimeLocal(assignment.assigned_at)}
          </Text>

          {assignment.assigned_to_user && (
            <Space size="small">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Assigned to:
              </Text>
              <Text style={{ fontSize: 12 }}>
                {assignment.assigned_to_user.name}
              </Text>
            </Space>
          )}

          {assignment.assigned_by_user && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              By: {assignment.assigned_by_user.name}
            </Text>
          )}

          {assignment.deadline && (
            <Space size="small">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Deadline:
              </Text>
              <Text style={{ fontSize: 12 }}>
                {commonHelper.displayDateTimeLocal(assignment.deadline)}
              </Text>
            </Space>
          )}

          {assignment.completed_at && (
            <Space size="small">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Completed:
              </Text>
              <Text style={{ fontSize: 12 }}>
                {commonHelper.displayDateTimeLocal(assignment.completed_at)}
              </Text>
            </Space>
          )}
        </Space>
      )
    };
  };

  if (loading) {
    return (
      <Card title="Timeline">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <Card title="Timeline">
        <Empty description="No timeline data available" />
      </Card>
    );
  }

  return (
    <Card title="Timeline" bordered={false}>
      <Timeline mode="left" items={timelineItems} />
    </Card>
  );
};

export default HazardTimeline;
